/** Bulk triage sweep bar (GIS Phase 2A, Task 11b, spec §4). Visible whenever
 *  the findings filter currently applied on `QueuePage` is non-empty — a
 *  bulk sweep always targets the ENTIRE matched set, never the whole
 *  (unfiltered) pending queue, so there is nothing to preview until the
 *  operator has actually scoped one down.
 *
 *  Two-step preview → confirm → execute, per spec §4:
 *   1. Preview (`POST /gis-queue/bulk/preview`) — count, up to 10 sample
 *      rows (the SAME nested `Finding` shape the findings list uses), the
 *      fills/overwrites breakdown (acknowledge only), and a `filter_hash`
 *      that must be echoed VERBATIM to execute.
 *   2. The operator types the EXACT count back (never a plain "are you
 *      sure?" checkbox) — Execute stays disabled until it matches.
 *   3. Execute (`POST /gis-queue/bulk/execute`) re-sends the same
 *      `{filter, action, note}` plus `filter_hash`. A 409 means the queue
 *      changed since preview (a matched row got resolved elsewhere, etc.) —
 *      the stale hash is dropped and the bar falls back to its pre-preview
 *      state so the operator must re-preview before trying again; it is
 *      never silently retried with the old hash.
 *
 *  Any filter/action/note edit after a preview exists invalidates it
 *  (`filter_hash` is a canonical hash over exactly that triple, spec §4
 *  R2-1) — the effect below drops the stale preview rather than let a
 *  guaranteed-409 execute be attempted.
 *
 *  Bulk supports Acknowledge and Dismiss ONLY (spec §4: value/FK writes stay
 *  single-row) — gated by `gis.triage.bulk` ALONE for both preview and
 *  execute; `gis.queue.view` is implied by `BulkBar` only ever mounting
 *  inside `QueuePage`, which already gates the whole page on it. A user
 *  missing `gis.triage.bulk` still SEES the bar (never silently hidden) —
 *  the Preview/Execute controls disable with a tooltip naming the missing
 *  capability, the same T10 idiom `ActionButtons` uses. */

import { useEffect, useState } from 'react'
import { executeBulkTriage, previewBulkTriage } from './api.ts'
import { isConflictError } from '../../api/client.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  hasCapability,
  type BulkAction,
  type BulkExecuteResponse,
  type BulkFilterFields,
  type BulkPreviewResponse,
  type FindingsQuery,
} from './types.ts'

const BULK_ACTION_LABELS: Record<BulkAction, string> = {
  acknowledge: 'Acknowledge',
  dismiss: 'Dismiss',
}

/** Maps the findings-list filter (`FindingsQuery`) to the bulk filter body
 *  (`schemas.gis_queue.BulkFilterFields`). Deliberately drops `resolution`/
 *  `limit`/`offset` — the bulk filter's `extra="forbid"` 422s on an unknown
 *  key, and the service hard-locks `resolution` to `'pending'` server-side
 *  regardless (that schema's own docstring, task-8 review H1). */
function toBulkFilter(filters: FindingsQuery): BulkFilterFields {
  return {
    discrepancy_type: filters.type ?? null,
    table_name: filters.table ?? null,
    field_name: filters.field_name ?? null,
    feeder_id: filters.feeder_id ?? null,
    run_id: filters.run_id ?? null,
    min_age_days: filters.min_age_days ?? null,
    platform_value_null: filters.platform_value_null ?? null,
  }
}

function hasActiveFilter(bulkFilter: BulkFilterFields): boolean {
  return Object.values(bulkFilter).some(v => v !== null && v !== undefined)
}

interface BulkBarProps {
  /** The findings list's currently-applied filter (`QueuePage`'s own
   *  `FindingsQuery` state) — mapped to the bulk filter body via
   *  `toBulkFilter`. */
  filters: FindingsQuery
  capabilities: readonly string[]
  /** Fired after a successful execute so `QueuePage` can show its own
   *  top-level feedback toast (batch id) and invalidate the findings list —
   *  mirrors `FindingRow`'s `onActionSuccess` bubble-up for the single-row
   *  flow. */
  onExecuteSuccess: (result: BulkExecuteResponse) => void
}

export function BulkBar({ filters, capabilities, onExecuteSuccess }: BulkBarProps) {
  const bulkFilter = toBulkFilter(filters)
  const filterKey = JSON.stringify(bulkFilter)
  const active = hasActiveFilter(bulkFilter)

  const [action, setAction] = useState<BulkAction>('acknowledge')
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<BulkPreviewResponse | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const missingCapability = !hasCapability(capabilities, 'gis.triage.bulk')
    ? 'gis.triage.bulk'
    : null

  // Any edit to the filter, action, or note after a preview exists
  // invalidates it (filter_hash is a canonical hash over exactly that
  // triple) — drop the stale preview so Execute can't fire against a hash
  // that no longer matches what would be sent. A no-op before any preview
  // exists (preview is already null).
  useEffect(() => {
    setPreview(null)
    setConfirmText('')
    setError(null)
  }, [filterKey, action, note])

  if (!active) return null

  const noteRequired = action === 'dismiss'
  const noteValid = !noteRequired || note.trim().length > 0
  const noteWithinLimit = note.length <= 2000
  const canPreview = !missingCapability && noteValid && noteWithinLimit && !busy
  const canExecute =
    !!preview && !missingCapability && !busy && confirmText.trim() === String(preview.count)

  function resetAll() {
    setPreview(null)
    setConfirmText('')
    setError(null)
    setNote('')
  }

  async function runPreview() {
    setBusy(true)
    setError(null)
    try {
      const result = await previewBulkTriage({
        filter: bulkFilter,
        action,
        note: noteRequired ? note.trim() : undefined,
      })
      setPreview(result)
      setConfirmText('')
    } catch (err) {
      setPreview(null)
      setError(friendlyError(err, 'preview the bulk action'))
    } finally {
      setBusy(false)
    }
  }

  async function runExecute() {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const result = await executeBulkTriage({
        filter: bulkFilter,
        action,
        note: noteRequired ? note.trim() : undefined,
        filter_hash: preview.filter_hash,
      })
      onExecuteSuccess(result)
      resetAll()
    } catch (err) {
      if (isConflictError(err)) {
        // Queue changed since preview — drop the stale hash entirely and
        // fall back to the pre-preview state; the operator must re-preview
        // before executing again (never silently retried with the old hash).
        setPreview(null)
        setConfirmText('')
      }
      setError(friendlyError(err, 'run the bulk action'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Bulk triage — current filters</h3>
        {preview && (
          <button
            type="button"
            onClick={resetAll}
            disabled={busy}
            className="text-xs text-gray-500 underline disabled:opacity-50"
          >
            Start over
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {!preview && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs text-gray-600 gap-1">
            Action
            <select
              value={action}
              onChange={e => setAction(e.target.value as BulkAction)}
              disabled={busy}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
            >
              <option value="acknowledge">Acknowledge</option>
              <option value="dismiss">Dismiss</option>
            </select>
          </label>

          {noteRequired && (
            <label className="flex flex-col text-xs text-gray-600 gap-1 flex-1 min-w-[240px]">
              Note (required)
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={2000}
                rows={1}
                disabled={busy}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                placeholder="Explain the decision — required for the audit trail."
              />
            </label>
          )}

          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={!canPreview}
            title={missingCapability ? `Requires the '${missingCapability}' capability` : undefined}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Preview {BULK_ACTION_LABELS[action].toLowerCase()}
          </button>
        </div>
      )}

      {preview && (
        <div className="space-y-3">
          <div className="text-sm text-gray-700">
            <strong>{preview.count}</strong> finding{preview.count === 1 ? '' : 's'} match
            {preview.count === 1 ? 'es' : ''} this filter — {BULK_ACTION_LABELS[action]}.
            {preview.breakdown && (
              <span className="text-gray-500">
                {' '}
                ({preview.breakdown.fills} empty-value fill{preview.breakdown.fills === 1 ? '' : 's'},{' '}
                {preview.breakdown.overwrites} overwrite{preview.breakdown.overwrites === 1 ? '' : 's'})
              </span>
            )}
          </div>

          {preview.sample.length > 0 && (
            <div className="border border-gray-100 rounded divide-y divide-gray-100 max-h-56 overflow-auto">
              {preview.sample.map(f => (
                <div
                  key={f.discrepancy.id}
                  className="px-2 py-1.5 text-xs text-gray-600 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {f.entity_summary ?? `${f.discrepancy.table_name} record ${f.discrepancy.record_id ?? '(none)'}`}
                  </span>
                  <span className="text-gray-400 shrink-0">
                    {f.discrepancy.field_name ?? f.discrepancy.discrepancy_type}
                  </span>
                </div>
              ))}
            </div>
          )}
          {preview.count > preview.sample.length && (
            <div className="text-xs text-gray-400">
              Showing {preview.sample.length} of {preview.count}.
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs text-gray-600 gap-1">
              Type {preview.count} to confirm
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                disabled={busy}
                className="w-32 text-sm border border-gray-300 rounded px-2 py-1"
              />
            </label>
            <button
              type="button"
              onClick={() => void runExecute()}
              disabled={!canExecute}
              title={missingCapability ? `Requires the '${missingCapability}' capability` : undefined}
              className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Execute bulk {BULK_ACTION_LABELS[action].toLowerCase()}
            </button>
            <button
              type="button"
              onClick={resetAll}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
